/**
 * Module-level deduplication for POST requests with identical cache keys.
 *
 * Unlike `deduplicateGet` (which clears entries immediately on settlement),
 * this keeps entries for `CACHE_TTL_MS` after the promise settles. This catches
 * sequential near-duplicates within the same initialization cycle — e.g. when
 * `useLoadPresets` and `fetchPage` both call `createDataQuery` with semantically
 * equivalent params a few hundred milliseconds apart.
 *
 * Callers provide a pre-computed cache key (typically URL + canonical body fingerprint).
 *
 * This is a module-level singleton — intentionally shared across all grid
 * instances on the page. Two grids with different gridCodes produce different
 * URLs → different keys → no cross-grid interference.
 */
const CACHE_TTL_MS = 5_000;
const cache = new Map<string, Promise<unknown>>();

/** @internal Clear all cached entries. Used only by test suites to prevent cross-test leakage. */
export function _clearDeduplicatePostCache(): void {
  cache.clear();
}
export function deduplicatePost<T>(key: string, request: () => Promise<T>): Promise<T> {
  const existing = cache.get(key);
  if (existing !== undefined) {
    return existing as Promise<T>;
  }
  const promise = request().finally(() => {
    setTimeout(() => {
      // Only clear if this is still the same entry (not overwritten by a newer request)
      if (cache.get(key) === promise) {
        cache.delete(key);
      }
    }, CACHE_TTL_MS);
  });
  cache.set(key, promise);
  return promise;
}